import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type HTMLAttributes,
} from 'react';

import { cn } from '../../lib/utils';
import { usePrivatePortalTarget } from './private-portal';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogPortal({
  children,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>) {
  const privateTarget = usePrivatePortalTarget();
  if (privateTarget === null) return null;
  return (
    <DialogPrimitive.Portal {...props} container={privateTarget ?? props.container}>
      {children}
    </DialogPrimitive.Portal>
  );
}

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Plain dim scrim, NO backdrop-blur — same decision as the Sheet scrim
      // (blur was gratuitous; the behind-panel content stays readable).
      'fixed inset-0 z-50 bg-foreground/40',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Class names for the scrim. The only real use is raising the whole layer
   * (overlay AND panel) above another z-50 surface — a Sheet, say — which has
   * to happen on BOTH halves or the panel floats over its own scrim.
   */
  overlayClassName?: string;
  /**
   * `true` when the caller supplies its own <DialogBody> scroll region: the
   * panel then never scrolls itself, so the header and the footer stay pinned
   * and only the middle moves. That is what keeps Create / Cancel on screen on
   * a phone or a short laptop no matter how tall the form is.
   *
   * Default `false` = the whole panel scrolls, which is right for short
   * dialogs and is what every pre-existing caller expects.
   */
  bodyScroll?: boolean;
}

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, overlayClassName, bodyScroll = false, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Never taller than the viewport (mobile landscape esp.) — clamp + scroll
        // internally so the header AND the action buttons stay reachable. Inset
        // 1rem from every edge below `sm` and always round (was sm:rounded-lg =
        // square-cornered edge-to-edge sheet on phones).
        //
        // `flex flex-col` (was `grid`) so a <DialogBody> can claim the leftover
        // height with `flex-1 min-h-0` and scroll on its own. For a panel with
        // no DialogBody the two lay out identically — one column, one gap.
        'fixed left-1/2 top-1/2 z-50 flex w-[calc(100%-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
        bodyScroll ? 'overflow-hidden' : 'overflow-y-auto',
        'gap-4 rounded-lg border border-border bg-background p-6 shadow-lg duration-200',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * The scrolling middle of a header / body / footer dialog. Requires
 * `bodyScroll` on the DialogContent, otherwise the panel scrolls too and the
 * two compete.
 *
 * `min-h-0` is load-bearing: a flex item defaults to `min-height: auto`, which
 * refuses to shrink below its content — without it the panel grows past
 * `max-h` and the footer leaves the screen again, which is the entire bug.
 */
export function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // The browser proof reads this to assert the scroll happens HERE and not
      // on the page (tests/browser-proof-settings/settings-modal-fit-proof).
      data-slot="dialog-body"
      // Bleed out to the panel edge and re-apply the padding inside, so the
      // scrollbar hugs the border instead of floating in a 24px gutter, and
      // focus rings on the edge fields are not clipped by the scroll box.
      className={cn('-mx-6 min-h-0 flex-1 overflow-y-auto px-6', className)}
      {...props}
    />
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // `shrink-0` + `pr-8`: never squeezed by a tall body, never running under
      // the absolutely-positioned close button in the corner.
      className={cn('flex shrink-0 flex-col space-y-1.5 pr-8 text-left', className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // `gap-2` (was `sm:space-x-2`) so the stacked phone layout separates the
      // buttons too; `shrink-0` so the footer is the last thing to give way.
      className={cn('flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
